import { roundMoney2 } from './money';

/** 按订购数量区间的运费档位（金额为订单行运费合计，不含税） */
export type FreightTier = {
  min_qty?: number | null;
  max_qty?: number | null;
  freight_amount: number;
};

export type NormalizedFreightTier = {
  min_qty: number | null;
  max_qty: number | null;
  freight_amount: number;
};

function parseOptionalQty(val: unknown): number | null {
  if (val == null || val === '') return null;
  const n = Math.floor(Number(val));
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
}

export function normalizeFreightTiers(raw: unknown): NormalizedFreightTier[] {
  if (!Array.isArray(raw)) return [];
  const tiers: NormalizedFreightTier[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const amount = Math.max(0, Number((item as FreightTier).freight_amount ?? 0));
    const minQty = parseOptionalQty((item as FreightTier).min_qty);
    const maxQty = parseOptionalQty((item as FreightTier).max_qty);
    if (amount <= 0 && minQty == null && maxQty == null) continue;
    tiers.push({ min_qty: minQty, max_qty: maxQty, freight_amount: amount });
  }
  return tiers.sort((a, b) => {
    const am = a.min_qty ?? 1;
    const bm = b.min_qty ?? 1;
    if (am !== bm) return am - bm;
    const ax = a.max_qty ?? Number.MAX_SAFE_INTEGER;
    const bx = b.max_qty ?? Number.MAX_SAFE_INTEGER;
    return ax - bx;
  });
}

function tierMatchesQty(tier: NormalizedFreightTier, qty: number): boolean {
  const minOk = tier.min_qty == null || qty >= tier.min_qty;
  const maxOk = tier.max_qty == null || qty <= tier.max_qty;
  return minOk && maxOk;
}

export function resolveFreightTotalFromTiers(raw: unknown, orderQuantity: unknown): number {
  const qty = Math.max(1, Math.floor(Number(orderQuantity ?? 1)));
  const tiers = normalizeFreightTiers(raw);
  if (!tiers.length) return 0;
  const matched = tiers.filter((t) => tierMatchesQty(t, qty));
  if (!matched.length) return 0;
  matched.sort((a, b) => (b.min_qty ?? 1) - (a.min_qty ?? 1));
  return Math.max(0, matched[0].freight_amount);
}

export function validateFreightTiers(raw: unknown, minOrderQty = 1): string | null {
  const tiers = normalizeFreightTiers(raw);
  if (!tiers.length) return null;

  const moq = Math.max(1, Math.floor(Number(minOrderQty ?? 1)));

  for (const tier of tiers) {
    if (tier.freight_amount < 0 || !Number.isFinite(tier.freight_amount)) {
      return '运费金额无效';
    }
    if (tier.min_qty != null && tier.max_qty != null && tier.min_qty > tier.max_qty) {
      return '运费区间起始数量不能大于结束数量';
    }
    if (tier.min_qty != null && tier.min_qty % moq !== 0) {
      return `运费区间起始数量须为起订量 ${moq} 的倍数`;
    }
    if (tier.max_qty != null && tier.max_qty % moq !== 0) {
      return `运费区间结束数量须为起订量 ${moq} 的倍数`;
    }
  }

  const ranged = tiers.filter((t) => t.min_qty != null || t.max_qty != null);
  for (let i = 0; i < ranged.length; i += 1) {
    for (let j = i + 1; j < ranged.length; j += 1) {
      const a = ranged[i];
      const b = ranged[j];
      const aMin = a.min_qty ?? 1;
      const aMax = a.max_qty ?? Number.MAX_SAFE_INTEGER;
      const bMin = b.min_qty ?? 1;
      const bMax = b.max_qty ?? Number.MAX_SAFE_INTEGER;
      if (aMin <= bMax && bMin <= aMax) {
        return '运费数量区间不能重叠，请调整采购数量范围';
      }
    }
  }

  const flatOnly = tiers.filter((t) => t.min_qty == null && t.max_qty == null);
  if (flatOnly.length > 1) return '统一运费只能设置一条（不填采购数量范围）';
  if (flatOnly.length === 1 && ranged.length > 0) {
    return '统一运费与按数量区间运费不能混用，请只保留一种设置方式';
  }

  if (ranged.length > 0) {
    const covered = (q: number) => tiers.some((t) => tierMatchesQty(t, q));
    if (!covered(moq)) {
      return `最少起订量为 ${moq} 件，请补充覆盖该数量的运费区间`;
    }
  }

  return null;
}

export function resolveLegacyFreightTotal(
  freightFee: unknown,
  freeShippingMinQty: unknown,
  orderQuantity: unknown,
): number {
  const fee = Math.max(0, Number(freightFee ?? 0));
  if (fee <= 0) return 0;
  const qty = Math.max(0, Number(orderQuantity ?? 0));
  const threshold = Number(freeShippingMinQty ?? 0);
  if (Number.isFinite(threshold) && threshold > 0 && qty >= threshold) {
    return 0;
  }
  return roundMoney2(fee * qty);
}

/**
 * 商城订单行运费合计。
 * 商城采购运费与供应商阶梯运费脱钩，固定为 0（供应商实际运费在发货时录入，仅用于服务费结算）。
 */
export function resolveLineFreightTotal(_input: {
  freightTiers?: unknown;
  freightFee?: unknown;
  freeShippingMinQty?: unknown;
  quantity: unknown;
}): number {
  return 0;
}

function formatTierLine(t: NormalizedFreightTier): string {
  const fee = `¥${t.freight_amount.toFixed(2)}`;
  if (t.min_qty != null && t.max_qty != null) return `${t.min_qty}–${t.max_qty} 件 ${fee}`;
  if (t.min_qty != null) return `≥${t.min_qty} 件 ${fee}`;
  if (t.max_qty != null) return `≤${t.max_qty} 件 ${fee}`;
  return `统一 ${fee}`;
}

/** 是否为按采购数量分档的阶梯运费（非单一统一运费） */
export function isTieredFreight(raw: unknown): boolean {
  const tiers = normalizeFreightTiers(raw);
  if (!tiers.length) return false;
  if (tiers.length > 1) return true;
  return tiers[0].min_qty != null || tiers[0].max_qty != null;
}

export type FreightDisplayInfo = {
  tiered: boolean;
  /** 0元 / ¥x.xx / 按数量区间计费 */
  primary: string;
  /** 阶梯运费各档说明，如「1–10 件 ¥5.00；≥11 件 ¥3.00」 */
  detail: string | null;
};

/** 商城运费展示：固定 0 元，与供应商阶梯运费无关 */
export function formatFreightDisplay(_raw?: unknown): FreightDisplayInfo {
  return { tiered: false, primary: '0元', detail: null };
}

export function formatFreightTierSummary(_raw?: unknown): string {
  return '0元';
}

export function createEmptyFreightTier(): FreightTier {
  return { min_qty: null, max_qty: null, freight_amount: 0 };
}

export function legacyFreightToTiers(
  freightFee: unknown,
  freeShippingMinQty: unknown,
  minOrderQty = 1,
): FreightTier[] {
  const fee = Math.max(0, Number(freightFee ?? 0));
  if (fee <= 0) return [];
  const moq = Math.max(1, Math.floor(Number(minOrderQty ?? 1)));
  const threshold = Number(freeShippingMinQty ?? 0);
  if (Number.isFinite(threshold) && threshold > moq) {
    return [
      {
        min_qty: moq,
        max_qty: threshold - 1,
        freight_amount: roundMoney2(fee * moq),
      },
      { min_qty: threshold, max_qty: null, freight_amount: 0 },
    ];
  }
  return [{ freight_amount: roundMoney2(fee * moq) }];
}
